import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { isSuperAdmin } from '@/utils/auth';
import { useState, useEffect } from 'react';
import { useUsersQuery, type UserListParams } from '@/api/users/getUsers';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, ChevronRight, Search, Users as UsersIcon } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Coerce to a positive integer; reject NaN, zero, negative, non-finite.
function toPositiveInt(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

// Coarse human-readable age relative to now. Years take precedence, then months,
// then weeks, then days. Returns '—' for invalid input. Exported for tests.
export function formatAccountAge(createdAt: string | null | undefined): string {
  if (!createdAt) return '—';
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return '—';
  const ms = Date.now() - created.getTime();
  if (ms < 0) return 'just now';
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days < 1) return 'today';
  if (days < 7) return `${days} day${days === 1 ? '' : 's'}`;
  const years = Math.floor(days / 365);
  if (years >= 1) {
    const months = Math.floor((days - years * 365) / 30);
    return months > 0 ? `${years}y ${months}m` : `${years} year${years === 1 ? '' : 's'}`;
  }
  const months = Math.floor(days / 30);
  if (months >= 1) return `${months} month${months === 1 ? '' : 's'}`;
  const weeks = Math.floor(days / 7);
  return `${weeks} week${weeks === 1 ? '' : 's'}`;
}

/**
 * True when the stored username is not a name the Player chose, but the fallback the auth
 * provider generated because no username claim was available.
 *
 * Worth distinguishing in the list: the fallback is `user_<last 8 of the SSO id>`, which reads
 * exactly like a real handle in a column of them, so without this an admin cannot tell who has
 * actually set a username. Mirrors `isPlaceholderUsername` in the backend's `utils/username.ts`
 * — kept as a local copy because no package is shared between the admin and the backend.
 */
export function isGeneratedUsername(
  username: string | null | undefined,
  ssoUserId: string | null | undefined
): boolean {
  const trimmed = username?.trim();
  if (!trimmed) return true;
  if (!ssoUserId) return false;
  return trimmed === ssoUserId || trimmed === `user_${ssoUserId.slice(-8)}`;
}

export const Route = createFileRoute('/dashboard/users/')({
  beforeLoad: () => {
    if (!isSuperAdmin()) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: UsersPage,
  validateSearch: (search: Record<string, unknown>): UserListParams => ({
    page: toPositiveInt(search.page),
    limit: toPositiveInt(search.limit),
    search: typeof search.search === 'string' ? search.search : undefined,
    sort:
      search.sort === 'last_played_at' ||
      search.sort === 'created_at' ||
      search.sort === 'total_winnings'
        ? (search.sort as 'last_played_at' | 'created_at' | 'total_winnings')
        : undefined,
  }),
});

const PAGE_SIZE = 50;

function UsersPage() {
  const navigate = useNavigate();
  const params = Route.useSearch();

  const [searchInput, setSearchInput] = useState(params.search ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(params.search ?? '');

  // Sync local input state when the URL search param changes externally
  // (browser back/forward, jumping to a bookmarked URL, etc.).
  useEffect(() => {
    const next = params.search ?? '';
    setSearchInput(prev => (prev === next ? prev : next));
    setDebouncedSearch(prev => (prev === next ? prev : next));
  }, [params.search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const page = params.page ?? 1;
  const sort = params.sort ?? 'created_at';

  const queryParams: UserListParams = {
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch || undefined,
    sort,
  };

  const { data, isLoading, isFetching } = useUsersQuery(queryParams);

  const setPage = (next: number) => {
    navigate({
      to: '/dashboard/users',
      search: { ...params, page: next, search: debouncedSearch || undefined, sort },
    });
  };

  const setSort = (next: 'created_at' | 'last_played_at' | 'total_winnings') => {
    navigate({
      to: '/dashboard/users',
      search: { ...params, sort: next, page: 1 },
    });
  };

  // Reset to page 1 whenever the debounced search term changes
  useEffect(() => {
    if ((params.search ?? '') !== debouncedSearch) {
      navigate({
        to: '/dashboard/users',
        search: { ...params, search: debouncedSearch || undefined, page: 1 },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const totalPages = data?.totalPages ?? 1;
  const totalUsers = data?.totalUsers ?? 0;
  const isApprox = data?.isApproximateCount ?? false;

  // If a stale URL or hand-typed ?page=999 lands beyond the result set,
  // bounce to the last valid page instead of showing an empty table.
  useEffect(() => {
    if (!data) return;
    if (page > totalPages && totalPages >= 1) {
      navigate({
        to: '/dashboard/users',
        search: { ...params, page: totalPages },
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, page, totalPages]);

  return (
    <PageContainer>
      <Breadcrumbs overrides={{ users: 'Users' }} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <UsersIcon className="h-6 w-6" />
            Users
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Search end users and view their full activity.
          </p>
        </div>
        <Badge variant="secondary" className="text-base px-4 py-1.5">
          {isApprox ? '~' : ''}
          {totalUsers.toLocaleString()} total users
        </Badge>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by SSO ID, user ID or username…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>
        <Select
          value={sort}
          onValueChange={v => setSort(v as 'created_at' | 'last_played_at' | 'total_winnings')}
        >
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at">Sort: Newest signups</SelectItem>
            <SelectItem value="last_played_at">Sort: Recently played</SelectItem>
            <SelectItem value="total_winnings">Sort: Top winners</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <ContentCard>
        <Table>
          <TableHeader className="[&_tr]:border-b-0">
            <TableRow>
              <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold">
                SSO ID
              </TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Username</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Level</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Signed up</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Last played</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                Age of account
              </TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold text-right">
                Entries
              </TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Location</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold text-right">
                Cash winnings
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 9 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : !data || data.users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              data.users.map(u => (
                <TableRow
                  key={u.id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() =>
                    navigate({
                      to: '/dashboard/users/$id',
                      params: { id: u.id },
                    })
                  }
                >
                  <TableCell className="font-mono text-xs">{u.ssoUserId || '—'}</TableCell>
                  <TableCell className="text-sm">
                    {isGeneratedUsername(u.username, u.ssoUserId) ? (
                      <span className="text-muted-foreground italic">Not set</span>
                    ) : (
                      u.username
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{u.level || '—'}</TableCell>
                  <TableCell className="text-sm">
                    <TzDateDisplay
                      utcDate={u.createdAt}
                      formatString="MMM d, yyyy"
                      showUtcBadge={false}
                    />
                  </TableCell>
                  <TableCell className="text-sm">
                    {u.lastPlayedAt ? (
                      <TzDateDisplay
                        utcDate={u.lastPlayedAt}
                        formatString="MMM d, yyyy"
                        showUtcBadge={false}
                      />
                    ) : (
                      <span className="text-muted-foreground">Never</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{formatAccountAge(u.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    {Number(u.totalEntries).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-sm">{u.location || '—'}</TableCell>
                  <TableCell className="text-right font-medium">
                    {Number(u.totalWinnings ?? 0) > 0
                      ? `£${Number(u.totalWinnings).toFixed(2)}`
                      : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ContentCard>

      <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
        <span>
          {isFetching && !isLoading ? 'Refreshing… ' : ''}
          Page {page} of {totalPages}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}
